import { Filter, type CalendarFilterDateType } from '@vrplatform/components';
import { day } from '@vrplatform/utils';
import { SegmentedControl } from '@mantine/core';
import { useMemo } from 'react';
import {
  type BalanceSheetTableFilterType,
  type BalanceSheetTableFilterView,
  useBalanceSheetTableFilter,
} from './useBalanceSheetTableFilter';
import { useJournalEntriesMinMaxQuery } from '@vrplatform/data';
import { CustomFilter } from '../../../components';

const Date = () => {
  const {
    filter: { type },
  } = useBalanceSheetTableFilter();

  if (type === 'year') {
    return <YearRange />;
  }

  return <MonthRange />;
};

const MonthRange = () => {
  const { filter, setDate } = useBalanceSheetTableFilter();

  const setValue = (v: CalendarFilterDateType | undefined | null) => {
    if (!v || !v[0]) return;

    const startDate = day(v[0]).yyyymmdd();
    const endDate =
      v[0] || v[1]
        ? day(v[1] || v[0])
            .endOf('month')
            .yyyymmdd()
        : undefined;

    if (!endDate) return;

    setDate({
      startDate: startDate,
      endDate: endDate,
    });
  };

  return (
    <Filter.Month
      label="Month"
      value={[day(filter.startDate).toDate(), day(filter.endDate).toDate()]}
      type="range"
      setValue={setValue}
    />
  );
};

const YearRange = () => {
  const { filter, setDate } = useBalanceSheetTableFilter();

  const queryData = useJournalEntriesMinMaxQuery({ range: 'year' });
  const data = queryData.data;

  const setValue = (v: CalendarFilterDateType | undefined | null) => {
    if (!v || !v[0]) return;

    const startDate = day(v[0]).yyyymmdd();
    const endDate =
      v[0] || v[1]
        ? day(v[1] || v[0])
            .endOf('year')
            .yyyymmdd()
        : undefined;

    if (!endDate) return;

    setDate({
      startDate: startDate,
      endDate: endDate,
    });
  };

  return (
    <Filter.Year
      label="Year"
      value={[day(filter.startDate).toDate(), day(filter.endDate).toDate()]}
      type="range"
      minDate={data?.minDate ? day(data.minDate).toDate() : undefined}
      maxDate={data?.maxDate ? day(data.maxDate).toDate() : undefined}
      setValue={setValue}
    />
  );
};

const Book = () => {
  const { filter, setFilter } = useBalanceSheetTableFilter();

  return (
    <CustomFilter.Book
      book={filter.ledger}
      setBook={(ledger) => setFilter({ ledger })}
    />
  );
};

const TYPE_OPTIONS: Record<BalanceSheetTableFilterType, string> = {
  month: 'By Month',
  quarter: 'By Quarter',
  year: 'By Year',
};

const Type = () => {
  const { filter, setType } = useBalanceSheetTableFilter();

  const data = useMemo(() => {
    return Object.entries(TYPE_OPTIONS).map(([value, label]) => ({
      label,
      value,
    }));
  }, []);

  return (
    <SegmentedControl
      data={data}
      size="sm"
      value={filter.type}
      onChange={(value) => setType(value as BalanceSheetTableFilterType)}
      styles={(theme) => ({
        innerLabel: {
          paddingInline: theme.spacing.xs,
        },
      })}
    />
  );
};

const VIEW_OPTIONS: Record<BalanceSheetTableFilterView, string> = {
  'non-zero-rows': 'Non-zero Rows',
  'non-zero-columns': 'Non-zero Columns',
};

const View = () => {
  const { filter, setFilter } = useBalanceSheetTableFilter();

  const data = useMemo(() => {
    return Object.entries(VIEW_OPTIONS).map(([value, label]) => ({
      label,
      value: value as BalanceSheetTableFilterView,
    }));
  }, []);

  const value = data.filter((i) => filter.view?.includes(i.value));

  return (
    <Filter.Select
      label="View"
      type="multiple"
      value={value}
      setValue={(v) =>
        setFilter({ view: v.length ? v.map((i) => i.value) : [] })
      }
      data={{
        options: data,
      }}
    />
  );
};

export const BalanceSheetTableFilter = {
  Date,
  Book,
  Type,
  View,
};
